import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ICurator } from "@src/types/curator";
import {
  emptyInitialStateShopCurator,
  IShopCurator,
} from "@src/types/shopCurator";
import { IAuthUser, IRole } from "@src/types/user";

export interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  user: IAuthUser | null;
  role: IRole | null;
  curator: ICurator | null;
  shopCurator: IShopCurator;
  errorMessage: string | null;
  loading: boolean;
  resetPasswordEmailSent: boolean;
  resetPasswordStatus: "success" | "error" | null;
}

export const authInitialState: AuthState = {
  isAuthenticated: false,
  token: null,
  user: null,
  role: null,
  curator: null,
  shopCurator: emptyInitialStateShopCurator,
  errorMessage: null,
  loading: false,
  resetPasswordEmailSent: false,
  resetPasswordStatus: null,
};

export const authSlice = createSlice({
  name: "auth",
  initialState: authInitialState,
  reducers: {
    resetPasswordEmailSent(state, action: PayloadAction<boolean>) {
      state.resetPasswordEmailSent = action.payload;
    },
    resetPasswordStatus(
      state,
      action: PayloadAction<"success" | "error" | null>
    ) {
      state.resetPasswordStatus = action.payload;
    },
    login(
      state,
      action: PayloadAction<{
        token: string;
        user: IAuthUser;
        curator: ICurator | null;
        role: IRole | null;
        shopCurator: IShopCurator | null;
      }>
    ) {
      state.isAuthenticated = true;
      state.token = action.payload.token;
      state.user = action.payload.user;
      state.curator = action.payload.curator || null;
      state.shopCurator =
        action.payload.shopCurator || emptyInitialStateShopCurator;
      state.errorMessage = null;
      state.role = action.payload.role || null;
    },
    logout(state) {
      state.isAuthenticated = false;
      state.token = null;
      state.user = null;
      state.curator = null;
      state.errorMessage = null;
    },
    setError(state, action: PayloadAction<string>) {
      state.errorMessage = action.payload;
    },
    clearError(state) {
      state.errorMessage = null;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    resetAuthState(state) {
      state.isAuthenticated = false;
      state.token = null;
      state.user = null;
      state.curator = null;
      state.errorMessage = null;
      state.loading = false;
      state.resetPasswordEmailSent = false;
      state.resetPasswordStatus = null;
    },
    setShopCurator(state, action: PayloadAction<IShopCurator | null>) {
      state.shopCurator = action.payload || emptyInitialStateShopCurator;
    },
    // Merge partial fields into the persisted curator (e.g. receiveMessages)
    // so settings changed at runtime survive a refresh instead of reverting to
    // the login-time snapshot rehydrated by redux-persist.
    updateAuthCurator(state, action: PayloadAction<Partial<ICurator>>) {
      if (state.curator) {
        state.curator = { ...state.curator, ...action.payload };
      }
    },
  },
});

export const {
  login,
  logout,
  setError,
  clearError,
  setLoading,
  resetPasswordEmailSent,
  resetPasswordStatus,
  resetAuthState,
  setShopCurator,
  updateAuthCurator,
} = authSlice.actions;
export default authSlice.reducer;
